import React from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { useDispatch, useSelector } from 'react-redux';
import { VIEWER_MODE } from './store/enums';
import { viewerMode, setViewerMode, panelFilter, setFilterPanelView } from './store/documentViewerReducer';
import { Stack, ButtonGroup, IconButton, Modal, Fade, Box, Tooltip } from '@mui/material';
import ViewDayOutlinedIcon from '@mui/icons-material/ViewDayOutlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import AddIcon from '@mui/icons-material/Add';
import { MoreInfo, Publish } from './assets/icons';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import CloseIcon from '@mui/icons-material/Close';
import { DocUploadForm } from './docUpload/docUploadForm';
import AgyleTextField from '../ui/fields/agyleTextField';
import moment from 'moment';
import { setFilter } from '../../store/reducers/wardMap';

const useStyles = makeStyles((theme) => ({
    root: {
        overflowX: 'auto',
        paddingLeft: '12px',
        paddingRight: '12px',
        paddingTop: '6px',
        paddingBottom: '6px',
    },
    button: {
        background: theme.palette.documentViewer.buttonsBackgroundColor,
        borderRadius: '6px',
        '& svg': {
            fontSize: '20px',
        },
    },
    buttonClose: {
        background: 'transparent',
        borderRadius: '6px',
        '& svg': {
            fontSize: '20px',
        },
    },
    buttonGroup: {
        background: theme.palette.documentViewer.buttonsBackgroundColor,
        borderRadius: '6px',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '4px',
        gap: '4px',
        marginTop: '4px',
        marginBottom: '4px',
    },
    buttonInGroup: {
        width: '24px',
        height: '24px',
        borderRadius: '6px',
        '& svg': {
            fontSize: '18px',
        },
        '&.selected': {
            background: theme.palette.documentViewer.selectedInGroupBackgroundColor,
            color: theme.palette.documentViewer.selectedInGroupColor,
        },
    },
    uploadBtn: {
        color: theme.palette.documentViewer.thumbnailBackPageBackgroundColor,
        background: theme.palette.primary.main,
        '&:hover': {
            background: theme.palette.primary.dark,
        },
    },
    OnBtn: {
        color: theme.palette.primary.main,
    },
    uploadModal: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        backgroundColor: theme.palette.documentViewer.tabBackgroundColor,
        borderWidth: '2px',
        borderStyle: 'solid',
        borderColor: theme.palette.documentViewer.buttonsBackgroundColor,
        padding: '16px',
        borderRadius: '8px',
    },
    uploadModalTitle: {
        fontSize: '18px',
        fontWeight: 'bold',
    },
    uploadModalTitleStack: {
        marginBottom: '8px',
        borderBottomStyle: 'solid',
        borderBottomWidth: '1px',
        borderBottomColor: theme.palette.documentViewer.buttonsBackgroundColor,
        paddingBottom: '16px',
        paddingLeft: '16px',
        paddingRight: '16px',
        marginLeft: '-16px',
        marginRight: '-16px',
    },
    searchInput: {
        padding: '6px 6px',
        fontWeight: '200',
        fontSize: '14px',
        lineHeight: '24px',
        '&::placeholder': {
            fontSize: '12px !important',
            lineHeight: '22px',
        },
    },
}));

export function MainToolbar(props) {
    const classes = useStyles();
    const dispatch = useDispatch();
    const mode = useSelector(viewerMode);
    const panelVisible = useSelector(panelFilter);
    const { t } = useTranslation();
    const patientName = useSelector((state) => _.get(state, 'patient.patient.name', 'Patient Name'));
    const catalog = useSelector((state) => _.get(state, `documentViewer.catalogId`, 'ID'));
    const attendances = useSelector((state) => _.get(state, `documentViewer.attendances`, []));
    const referrals = useSelector((state) => _.get(state, `documentViewer.referrals`, []));
    const [openUpload, setOpenUpload] = React.useState(false);
    const handleOpenModal = () => setOpenUpload(true);
    const handleCloseModal = () => setOpenUpload(false);
    const [filesToUploadCount, setFilesToUploadCount] = React.useState(0);
    const [searchInsideDocMode, setSearchInsideDocMode] = React.useState(false);
    const [textToSearch, setTextToSearch] = React.useState('');
    const searchInDocs = (searchText) => {
        dispatch(setFilter({ text: searchText }));
    };
    const onOffFilterPanel = () => {
        dispatch(setFilterPanelView({ visible: !panelVisible }));
    };

    return (
        <>
            <Stack direction={'row'} spacing={1} justifyContent={'space-between'} alignItems={'center'} className={classes.root}>
                <ButtonGroup variant="contained" aria-label="Display mode" className={classes.buttonGroup}>
                    <IconButton
                        className={`${classes.buttonInGroup} ${mode === VIEWER_MODE.DOC_VIEW ? 'selected' : ''}`}
                        onClick={() => dispatch(setViewerMode({ mode: VIEWER_MODE.DOC_VIEW }))}
                        size="small"
                    >
                        <ViewDayOutlinedIcon />
                    </IconButton>
                    <IconButton
                        className={`${classes.buttonInGroup} ${mode === VIEWER_MODE.LIST ? 'selected' : ''}`}
                        onClick={() => dispatch(setViewerMode({ mode: VIEWER_MODE.LIST }))}
                        size="small"
                    >
                        <FormatListBulletedIcon />
                    </IconButton>
                </ButtonGroup>
                {!searchInsideDocMode && (
                    <Stack direction={'row'} spacing={'8px'}>
                        <IconButton className={classes.button} size={'small'}>
                            <Publish />
                        </IconButton>
                        <IconButton className={classes.button} size={'small'}>
                            <MoreInfo />
                        </IconButton>
                        {/*<IconButton className={classes.button} size={'small'}>*/}
                        {/*    <StarOutlineIcon />*/}
                        {/*</IconButton>*/}
                        <IconButton
                            className={classes.button}
                            size={'small'}
                            onClick={() => {
                                setTextToSearch('');
                                setSearchInsideDocMode(true);
                            }}
                        >
                            <SearchIcon />
                        </IconButton>
                        <IconButton
                            className={`${classes.button} ${panelVisible ? classes.OnBtn : ''}`}
                            size={'small'}
                            onClick={onOffFilterPanel}
                        >
                            <FilterListIcon />
                        </IconButton>
                        <IconButton className={`${classes.button} ${classes.uploadBtn}`} size={'small'} onClick={handleOpenModal}>
                            <AddIcon />
                        </IconButton>
                    </Stack>
                )}
                {searchInsideDocMode && (
                    <Stack direction={'row'} spacing={'4px'} alignItems={'center'} justifyContent={'space-between'}>
                        <AgyleTextField
                            fullWidth
                            name={`search_input_${moment().format('YYYYMMDDhhmmssSSS')}`}
                            placeholder={t('Search...')}
                            startAdornment={<SearchIcon width={'18px'} height={'18px'} />}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    searchInDocs(e.target.value);
                                }
                            }}
                            value={textToSearch}
                            onChange={(e) => setTextToSearch(e.target.value)}
                            noBorder={false}
                            inputPropsClasses={classes.searchInput}
                        />
                        <Tooltip title={t('Close search')}>
                            <IconButton
                                className={classes.buttonClose}
                                size={'small'}
                                variant="outlined"
                                onClick={() => {
                                    setSearchInsideDocMode(false);
                                    dispatch(setFilter({ text: null }));
                                }}
                            >
                                <CloseIcon />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                )}
            </Stack>
            <Modal aria-labelledby="modal-upload" open={openUpload}>
                <Fade in={openUpload}>
                    <Box className={classes.uploadModal} width={filesToUploadCount === 0 ? 600 : 980}>
                        <Stack
                            direction={'row'}
                            justifyContent={'space-between'}
                            alignItems={'center'}
                            alignContent={'center'}
                            className={classes.uploadModalTitleStack}
                        >
                            <Box className={classes.uploadModalTitle}>
                                {`${t('Upload Document')} ${t('for')} ${patientName} [${catalog}]`}
                            </Box>
                            <IconButton onClick={handleCloseModal}>
                                <CloseIcon />
                            </IconButton>
                        </Stack>
                        <DocUploadForm
                            uploadFilesCount={setFilesToUploadCount}
                            catalog={catalog}
                            attendances={attendances}
                            referrals={attendances}
                        />
                    </Box>
                </Fade>
            </Modal>
        </>
    );
}
